class Clock {
  private startTime = -1;
  private previousTime = -1;
  private currentTime = -1;

  constructor(autoStart = true) {
    if (autoStart) {
      this.reset();
    }
  }

  public tick() {
    if (this.startTime === -1) {
      this.startTime = performance.now();
    }
    if (this.previousTime === -1) {
      this.previousTime = performance.now();
    }
    this.previousTime = this.currentTime;
    this.currentTime = performance.now();
  }

  public get deltaMs() {
    return this.currentTime - this.previousTime;
  }

  public get elapsedTimeMs() {
    return this.startTime - this.currentTime;
  }

  public get currentTimeMs() {
    return this.currentTime;
  }

  public reset() {
    this.startTime = performance.now();
    this.previousTime = performance.now();
    this.currentTime = performance.now();
  }
}

export type FrameRateMonitorCallback = (
  wideAverageFrameTime: number,
  narrowAverageFrameTime: number,
  message: string,
) => void;

export class FrameRateMonitor {
  private timestamps: number[] = [];
  private _wideWindowMs: number;
  private _narrowWindowMs: number;
  private dropThreshold: number;
  private callbacks = new Set<FrameRateMonitorCallback>();
  private hasEnoughData = false;

  constructor(wideWindowMs = 20_000, narrowWindowMs = 7500, dropThreshold = 0.66) {
    this._wideWindowMs = wideWindowMs;
    this._narrowWindowMs = narrowWindowMs;
    this.dropThreshold = dropThreshold;
  }

  public reset = () => {
    this.timestamps = [];
    this.hasEnoughData = false;
  };

  public update = (timestamps: number) => {
    this.timestamps.push(timestamps);

    const thisFrameTimeMs = this.timestamps[this.timestamps.length - 1];
    this.hasEnoughData = thisFrameTimeMs - this.timestamps[0] >= this.wideWindowMs;

    if (!this.hasEnoughData) {
      return;
    }

    this.timestamps = this.timestamps.filter((t) => t > thisFrameTimeMs - this.wideWindowMs);
    const [wideAverageFrameTime, narrowAverageFrameTime] = this.averageFrameTimes;

    if (narrowAverageFrameTime * this.dropThreshold > wideAverageFrameTime) {
      for (const callback of this.callbacks) {
        const message = `Average frame time dropped from ${wideAverageFrameTime}ms over ${(this.wideWindowMs / 1000).toFixed(2)}s to ${narrowAverageFrameTime}ms over ${(this.narrowWindowMs / 1000).toFixed(2)}s`;
        callback(wideAverageFrameTime, narrowAverageFrameTime, message);
      }
      this.reset();
    }
  };

  public addCallback(callback: FrameRateMonitorCallback) {
    this.callbacks.add(callback);
  }

  public removeCallback(callback: FrameRateMonitorCallback) {
    this.callbacks.delete(callback);
  }

  public get averageFrameTimes(): [wideWindowAverage: number, narrowWindowAverage: number] {
    const thisFrameTimeMs = this.timestamps[this.timestamps.length - 1];

    if (!this.hasEnoughData) {
      return [0, 0];
    }

    const wideWindowSample = this.timestamps;
    const narrowWindowSample = this.timestamps.filter((t) => t > thisFrameTimeMs - this.narrowWindowMs);

    const wideWindowDeltas: number[] = [];
    for (let i = 1; i < wideWindowSample.length; i++) {
      wideWindowDeltas.push(wideWindowSample[i] - wideWindowSample[i - 1]);
    }
    const narrowWindowDeltas: number[] = [];
    for (let i = 1; i < narrowWindowSample.length; i++) {
      narrowWindowDeltas.push(narrowWindowSample[i] - narrowWindowSample[i - 1]);
    }

    const wideAverageFrameTime = wideWindowDeltas.reduce((acc, t) => acc + t, 0) / wideWindowDeltas.length;
    const narrowAverageFrameTime = narrowWindowDeltas.reduce((acc, t) => acc + t, 0) / narrowWindowDeltas.length;

    return [wideAverageFrameTime, narrowAverageFrameTime];
  }

  public get wideWindowMs(): number {
    return this._wideWindowMs;
  }

  public get narrowWindowMs(): number {
    return this._narrowWindowMs;
  }
}

export default Clock;
